import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, BookOpen, ArrowRight } from 'lucide-react';
import { blogApi } from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const PAGE_SIZE = 6;

function PageBranding({ branding, pageName }) {
  const brandColor = branding?.primary_color;
  const tileStyle = brandColor ? { backgroundColor: brandColor, color: '#fff' } : undefined;
  return (
    <div className="text-center space-y-2">
      {branding?.logo_url ? (
        <img
          src={branding.logo_url}
          alt={branding.app_name}
          className="inline-block w-14 h-14 rounded-2xl object-cover mb-1"
        />
      ) : (
        <div
          className="inline-flex w-14 h-14 rounded-2xl bg-primary text-primary-foreground items-center justify-center mb-1"
          style={tileStyle}
        >
          <BookOpen size={28} />
        </div>
      )}
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
        {pageName}
      </h1>
      <p className="text-sm text-muted-foreground">Latest articles & stories</p>
    </div>
  );
}

export default function BlogPublicList() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [items, setItems] = useState([]);
  const [pageNum, setPageNum] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState('');

  const loadPage = useCallback(async () => {
    try {
      const { data } = await blogApi.publicPage(slug);
      setPage(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Blog page not found');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const loadList = useCallback(async (p = 1) => {
    setListLoading(true);
    try {
      const { data } = await blogApi.publicList(slug, { page: p, size: PAGE_SIZE });
      setItems(data.blogs || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPageNum(data.page || p);
    } catch {
      // Page exists but list endpoint may fail (e.g. no posts). Silent.
    } finally {
      setListLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadPage(); }, [loadPage]);
  useEffect(() => { if (page) loadList(1); }, [page, loadList]);

  useEffect(() => {
    if (page?.page_name) {
      const brand = page?.branding?.app_name || 'CredBloom';
      document.title = `${page.page_name} · ${brand}`;
    }
  }, [page]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10">
        <Loader2 size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center space-y-3">
            <AlertCircle size={36} className="text-red-500 mx-auto" />
            <h2 className="text-lg font-semibold">Blog page not found</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const branding = page?.branding || { app_name: 'CredBloom', primary_color: null, logo_url: null };
  const brandColor = branding.primary_color;

  return (
    <div className="min-h-screen px-4 py-10 bg-gradient-to-br from-primary/5 via-background to-primary/10">
      <div className="max-w-4xl mx-auto space-y-8">
        <PageBranding branding={branding} pageName={page?.page_name} />

        {listLoading ? (
          <div className="text-center py-16">
            <Loader2 size={24} className="animate-spin text-primary inline" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
              <BookOpen size={32} className="mx-auto mb-2 opacity-60" />
              <p>No posts published yet. Check back soon.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="public-blogs-list">
            {items.map((b) => (
              <Card key={b.id} className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`public-blog-${b.blog_slug}`}>
                <div className="flex flex-col md:flex-row">
                  {b.image_url && (
                    <div className="md:w-56 shrink-0">
                      <img src={b.image_url} alt={b.title} className="w-full h-44 md:h-full object-cover" />
                    </div>
                  )}
                  <CardContent className="flex-1 p-5 flex flex-col">
                    <p className="text-xs text-muted-foreground mb-1.5">
                      {b.published_at ? new Date(b.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    </p>
                    <h2 className="text-xl font-semibold leading-tight mb-2" style={{ fontFamily: 'Manrope' }}>
                      {b.title}
                    </h2>
                    {b.excerpt && (
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-3 flex-1">{b.excerpt}</p>
                    )}
                    <div>
                      <Link
                        to={`/blog/${slug}/${b.blog_slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                        style={brandColor ? { color: brandColor } : undefined}
                        data-testid={`read-more-${b.blog_slug}`}
                      >
                        Read more <ArrowRight size={14} />
                      </Link>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 pt-2" data-testid="public-pagination">
            <Button
              variant="outline"
              size="sm"
              disabled={pageNum <= 1}
              onClick={() => loadList(pageNum - 1)}
              data-testid="public-prev"
            >
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page <span className="font-medium text-foreground">{pageNum}</span> of <span className="font-medium text-foreground">{totalPages}</span>
              <span className="hidden sm:inline"> · {total} posts</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={pageNum >= totalPages}
              onClick={() => loadList(pageNum + 1)}
              data-testid="public-next"
            >
              Next
            </Button>
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground pt-6" data-testid="blog-footer-branding">
          Powered by {branding.app_name || 'CredBloom'}
        </p>
      </div>
    </div>
  );
}
